import { logger } from '@heidi/runtime/logger';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { invalidateSessionsQuery } from '@heidi/session/data';
import { getDefaultStore } from 'jotai';

import type { ServerMessage, VoiceContextHint } from '../../contracts/voice-protocol';
import {
  composerLinkedItemsAtom,
  openArtifactAtom,
} from '../../data/projects/artifacts/state';
import {
  activeWorkPanelProjectIdAtom,
  workPanelProjectIdAtomFamily,
} from '../../data/projects/workPanel';
import { getVoiceAppHost } from '../../data/voice/voice-app-host';
import { VoiceSocket } from '../../data/voice/voiceSocket';
import { isVoiceWorkspaceEnabled } from '../../flags';
import { invalidateLists as invalidateProjectLists } from '../projects/hooks';
import {
  CLINICAL_TASK_WRITE_TOOL_KEYS,
  invalidateCachesForAgentToolWrite,
} from '../projects/hooks/agentToolCacheInvalidation';
import { PcmPlayer } from './audio/pcmPlayer';
import { PcmRecorder } from './audio/pcmRecorder';
import { buildVoiceContextHint, voiceContextHintKey } from './contextHint';
import { autoDumpVoiceSession } from './debug/formatVoiceDebugDump';
import { useVoiceGuideStore } from './help/useVoiceGuideStore';
import { useVoiceHighlightStore } from './help/useVoiceHighlightStore';
import { useVoiceAssistantStore } from './store/useVoiceAssistantStore';
import { voiceActiveTabAtom } from './store/voiceActiveTabAtom';
import { hasToolForUiAction } from './toolIdentity';
import { applyConfiguredVoiceUiAction } from './ui/voice-ui-actions-registry';

/** Heidi Work / Projects routes — canvas can host ``ui.open_artifact``. */
const isHeidiWorkRoute = (pathname: string): boolean =>
  pathname.startsWith('/scribe/chat/projects');

// Local barge-in: only while Heidi is playing audio. RMS is normalised to
// [0,1]; frames are ~60ms (1440 samples @ 24 kHz).
//
// The trigger is relative to a live noise-floor estimate rather than an
// absolute level. Capture runs with autoGainControl + noiseSuppression, which
// puts ordinary conversational speech around 0.02–0.08 RMS on a laptop mic, so
// the previous fixed 0.12 gate sat above normal talk-over and real
// interruptions never registered.
const BARGE_IN_FLOOR_MARGIN = 3.5; // ~11 dB over the measured floor
const BARGE_IN_MIN_THRESHOLD = 0.02; // silent room: never trigger on nothing
// Ceiling so a loud room (or echo the AEC let through) can never raise the bar
// past what a clinician can shout over.
const BARGE_IN_MAX_THRESHOLD = 0.09;
// Asymmetric so the floor tracks quiet quickly but a few hundred ms of the
// clinician's own speech cannot drag it up and end their own streak.
const NOISE_FLOOR_ATTACK = 0.02;
const NOISE_FLOOR_DECAY = 0.3;
//
// Playback is all-or-nothing, and a false trigger costs the rest of Heidi's
// sentence (playback stays suppressed until the turn ends), so wait for
// sustained energy: clicks are 1–2 frames, real talk-over runs hundreds of ms.
const BARGE_IN_CONFIRM_FRAMES = 5; // ~300ms sustained — a real interruption
const BARGE_IN_QUIET_RESET = 3; // ~180ms quiet before the streak dies

// Handing the mic to visit transcription: capture must stop at once, but the
// speaker doesn't have to. Let whatever Heidi is mid-sentence on land, capped
// so a long answer can't hold the session (or the panel) open.
const MIC_HANDOFF_MAX_DRAIN_MS = 2200;
const MIC_HANDOFF_POLL_MS = 120;

export { CLINICAL_TASK_WRITE_TOOL_KEYS };

/**
 * If no opening `speaking` arrives after the socket becomes active, flip the
 * status out of “Starting…” rather than leave it stuck (suppressed greeting,
 * dropped frame). Conservative: only covers "never began", not a slow greeting
 * already underway. Mic audio opens once the greeting turn starts (`speaking`)
 * so barge-in works during the hello without streaming the pre-audio Starting…
 * window — and without coupling outbound mic to playback gaps / local flush.
 */
export const GREETING_FALLBACK_MS = 10_000;

/**
 * Singleton that owns the live voice session's imperative resources (mic
 * recorder, reconnecting socket, audio player) and projects state into the
 * Zustand store. UI components stay declarative and read from the store.
 */
class VoiceController {
  private recorder: PcmRecorder | null = null;
  private socket: VoiceSocket | null = null;
  private player: PcmPlayer | null = null;
  private active = false;
  // Keeps the backend's working context in sync as the clinician navigates
  // or changes the Work canvas / linked chip while a voice session is open.
  private onRouteChange: (() => void) | null = null;
  private unsubscribeRoute: (() => void) | null = null;
  private unsubCanvasContext: (() => void) | null = null;
  private lastContextHintKey: string | null = null;
  private loudFrames = 0;
  private quietFrames = 0;
  /** Rolling ambient (and leaked-echo) level the clinician has to exceed. */
  private noiseFloor = BARGE_IN_MIN_THRESHOLD / BARGE_IN_FLOOR_MARGIN;
  /** After a local barge-in, drop inbound PCM until Heidi's turn ends. */
  private playbackSuppressed = false;
  /**
   * Opening-greeting status only — drives “Starting…” / “Heidi is speaking”
   * until the hello finishes. Outbound mic opens once `greetingStarted` so
   * barge-in works without streaming the pre-greeting Starting… window.
   */
  private greetingStarted = false;
  private greetingResponseDone = false;
  private greetingFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * First user turn for a user-initiated open (chip while connecting / deep
   * link). Consumed once on the opening ``init`` frame so the server skips
   * the canned hello.
   */
  private pendingSeedText: string | null = null;
  /** Ambient wake clip: sent after ``init.audioSeed`` then ``commit_audio``. */
  private pendingAudioSeed: Promise<ArrayBuffer> | null = null;
  /** Drop live mic frames until the seeded clip has been written. */
  private seedingAudio = false;

  private get store() {
    return useVoiceAssistantStore.getState();
  }

  async start(options?: {
    text?: string;
    audioSeed?: Promise<ArrayBuffer>;
  }): Promise<void> {
    if (this.active) return;
    this.active = true;

    // Optional first turn for a user-initiated open. Predefined UI copy only
    // (quick-start chips) — do not pass free-form / URL-decoded content here.
    const seed = options?.text?.trim();
    this.pendingSeedText = seed || null;
    this.pendingAudioSeed = options?.audioSeed ?? null;
    this.seedingAudio = false;

    const store = this.store;
    store.reset();
    store.setConnection('connecting');
    this.greetingStarted = false;
    this.greetingResponseDone = false;
    this.clearGreetingFallback();
    // Defer transcript paint until onOpen so a failed connect does not leave
    // a user turn that Heidi never received.

    this.player = new PcmPlayer((playing) => {
      this.store.setAssistantSpeaking(playing);
      if (!playing) this.maybeCompleteGreeting();
    });

    this.recorder = new PcmRecorder({
      onFrame: (pcm) => {
        // Belt-and-suspenders: worklet mute is async via postMessage; store is sync.
        if (this.store.muted) return;
        if (this.seedingAudio) return;
        // Pre-greeting Starting…: hold ambient connect noise. Once the greeting
        // turn has begun (`speaking`), keep streaming even across playback gaps
        // or a local flush — otherwise VAD soft-barge cancels when mic dies.
        if (!this.store.greetingComplete && !this.greetingStarted) return;
        this.maybeLocalBargeIn(pcm);
        this.socket?.sendAudio(pcm);
      },
      onError: (message) => {
        this.store.setError(message);
        void this.stop();
      },
      // Distinct from a generic error: drive the platform-aware recovery state
      // so the clinician can grant access and retry, rather than seeing a dead
      // "something went wrong" line (AGE-1625).
      onPermissionDenied: () => {
        this.store.setMicPermissionDenied(true);
        void this.stop();
      },
    });

    this.socket = new VoiceSocket({
      onOpen: () => {
        this.store.setConnection('active');
        const hint = this.currentHint();
        this.lastContextHintKey = voiceContextHintKey(hint);
        const seed = this.pendingSeedText;
        this.pendingSeedText = null;
        if (seed) {
          // User-initiated: no canned hello — skip Starting… gating.
          this.store.appendTranscript('user', seed);
          this.store.setGreetingComplete(true);
          this.clearGreetingFallback();
          this.socket?.sendControl({
            type: 'init',
            context: {
              ...hint,
              workspaceAvailable: isVoiceWorkspaceEnabled(),
            },
            text: seed,
          });
          return;
        }
        const audioSeed = this.pendingAudioSeed;
        this.pendingAudioSeed = null;
        if (audioSeed) {
          this.store.setGreetingComplete(true);
          this.clearGreetingFallback();
          this.socket?.sendControl({
            type: 'init',
            context: {
              ...hint,
              workspaceAvailable: isVoiceWorkspaceEnabled(),
            },
            audioSeed: true,
          });
          void this.sendAudioSeed(audioSeed).then(() => {
            void this.armBrowserMic();
          });
          return;
        }
        this.armGreetingFallback();
        this.socket?.sendControl({
          type: 'init',
          context: {
            ...hint,
            // Capability, not context: tells the server this client renders
            // the workspace overlay, so it may offer draft_session_document.
            workspaceAvailable: isVoiceWorkspaceEnabled(),
          },
        });
      },
      onControl: (message) => this.handleControl(message),
      onAudio: (pcm) => {
        if (this.playbackSuppressed) return;
        this.player?.enqueue(pcm);
      },
      onClose: (intentional) => {
        if (intentional) return;
        // Unintentional terminal close (reconnects exhausted or a 1008 policy
        // close). The session is dead, so release the mic/recorder/socket/
        // player instead of leaving the microphone live on a dead session.
        // stop() preserves an existing 'error' connection state.
        this.stop();
      },
      // onError is terminal (no auth token, or a 1008 policy close): the
      // socket will not retry, so release the mic instead of leaving capture
      // running against a dead connection. stop() preserves the error state.
      // Server-provided close reasons cross as text; local failure codes
      // stay codes so the panel can localize them at render time.
      onError: (error) => {
        this.store.setError(error.kind === 'server' ? error.text : error);
        this.stop();
      },
    });

    // Ambient wake still owns the native mic until the first-turn clip is
    // written. Starting getUserMedia here starves that capture, take times
    // out, and commit_audio never fires.
    if (this.pendingAudioSeed) {
      this.socket?.connect();
    } else if (!(await this.armBrowserMic())) {
      return;
    } else {
      this.socket?.connect();
    }
    // connect() can fail synchronously (e.g. signed out) -> onError -> stop().
    if (!this.active) return;

    this.onRouteChange = () => this.pushContextUpdate();
    this.unsubscribeRoute = getVoiceAppHost().subscribeRouteChange(this.onRouteChange);

    // Work canvas / composer linked chip can change without a route change —
    // that is the "current session" clinicians mean when they say "this one".
    const jotaiStore = getDefaultStore();
    const onCanvasContextChange = () => this.pushContextUpdate();
    const unsubArtifact = jotaiStore.sub(openArtifactAtom, onCanvasContextChange);
    const unsubLinked = jotaiStore.sub(composerLinkedItemsAtom, onCanvasContextChange);
    const unsubTab = jotaiStore.sub(voiceActiveTabAtom, onCanvasContextChange);
    this.unsubCanvasContext = () => {
      unsubArtifact();
      unsubLinked();
      unsubTab();
    };
  }

  /**
   * Give the mic to visit transcription without cutting Heidi off mid-word.
   *
   * Capture stops immediately — the visit recorder is about to call
   * getUserMedia and must not race us for the device — but playback is not cut
   * with it. Whatever Heidi is saying gets to land first. Killing capture and
   * playback in the same tick is what made "start transcribing" read as a
   * crash: her sentence was guillotined and the panel vanished mid-syllable.
   *
   * Resolves once the session is fully torn down, so the caller can wait to
   * close the panel. Recording start must NOT await this.
   */
  async handOffMicForVisitRecording(): Promise<void> {
    if (!this.active) return;
    this.recorder?.stop();
    this.recorder = null;
    this.store.setMicStream(null);
    const deadline = Date.now() + MIC_HANDOFF_MAX_DRAIN_MS;
    while (this.active && this.player?.isPlaying && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, MIC_HANDOFF_POLL_MS));
    }
    this.stop();
  }

  stop(): void {
    if (!this.active) return;
    this.active = false;
    this.loudFrames = 0;
    this.quietFrames = 0;
    this.noiseFloor = BARGE_IN_MIN_THRESHOLD / BARGE_IN_FLOOR_MARGIN;
    this.playbackSuppressed = false;
    this.greetingStarted = false;
    this.greetingResponseDone = false;
    this.pendingSeedText = null;
    this.pendingAudioSeed = null;
    this.seedingAudio = false;
    this.clearGreetingFallback();
    // Snapshot before teardown so the dump still has connection/error context.
    this.dumpSessionForDebug();
    if (this.unsubscribeRoute) {
      this.unsubscribeRoute();
      this.unsubscribeRoute = null;
      this.onRouteChange = null;
    }
    if (this.unsubCanvasContext) {
      this.unsubCanvasContext();
      this.unsubCanvasContext = null;
    }
    this.lastContextHintKey = null;
    try {
      this.socket?.close();
      this.recorder?.stop();
      this.player?.dispose();
    } catch (error) {
      logger.debug('[voice] error during controller stop', {
        errorType: error instanceof Error ? error.name : 'unknown',
      });
    } finally {
      this.socket = null;
      this.recorder = null;
      this.player = null;
      this.store.setMicStream(null);
      // A pending write approval is wired to the now-closed socket; drop it
      // so the confirm dialog doesn't outlive the session.
      this.store.setApproval(null);
      useVoiceGuideStore.getState().clearGuide();
      useVoiceHighlightStore.getState().clearHighlight();
      this.store.setSessionState('closed');
      if (this.store.connection !== 'error') {
        this.store.setConnection('idle');
      }
    }
  }

  /** Auto dump on End (local/dev download + always console + sessionStorage). */
  private dumpSessionForDebug(): void {
    const { timeline, connection, sessionState, error } = this.store;
    autoDumpVoiceSession(timeline, {
      exportedAt: new Date().toISOString(),
      connection,
      sessionState,
      surfacePath:
        typeof window !== 'undefined' ? getVoiceAppHost().readRoute().asPath : undefined,
      currentSessionId: this.currentHint().sessionId ?? getCurrentSessionId(),
      // Diagnostic dump: a local failure code is more useful than its copy.
      error: typeof error === 'string' ? error : (error?.kind ?? null),
    });
  }

  toggleMute(): void {
    const next = !this.store.muted;
    this.store.setMuted(next);
    this.recorder?.setMuted(next);
    this.socket?.sendControl({ type: next ? 'mute' : 'unmute' });
  }

  sendText(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Typed / chip turns interrupt the opening hello the same as talk-over.
    if (!this.store.greetingComplete) {
      this.store.setGreetingComplete(true);
      this.clearGreetingFallback();
    }
    if (this.player?.isPlaying) {
      this.playbackSuppressed = true;
      this.player.flush();
    }
    this.store.appendTranscript('user', trimmed);
    this.socket?.sendControl({ type: 'text', text: trimmed });
  }

  /**
   * Park a first user turn on the opening ``init`` frame (user-initiated open).
   *
   * Call while ``connecting`` so the seed rides with ``init`` and the server
   * skips the canned hello. Once ``active``, falls through to {@link sendText}.
   *
   * Pass only predefined UI copy (e.g. quick-start chip strings) — not
   * free-form or URL-decoded content. First seed wins while connecting;
   * transcript paints on socket open so a failed connect leaves no ghost turn.
   */
  seedOpen(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (this.store.connection === 'active') {
      this.sendText(trimmed);
      return;
    }
    // First seed wins — avoid diverging transcript vs init.text on overwrite.
    if (this.pendingSeedText) return;
    this.pendingSeedText = trimmed;
  }

  /** 100 ms of PCM16 mono at 24 kHz. */
  private static readonly AUDIO_SEED_FRAME_BYTES = 4800;

  private async armBrowserMic(): Promise<boolean> {
    const recorder = this.recorder;
    if (!recorder) {
      return false;
    }
    const started = await recorder.start();
    if (!this.active) {
      // stop() raced us while the mic permission prompt was pending. It
      // already released the socket/player; make sure the just-acquired
      // stream is released too.
      recorder.stop();
      return false;
    }
    if (!started) {
      this.stop();
      return false;
    }
    this.store.setMicStream(recorder.stream);
    return true;
  }

  private async sendAudioSeed(seed: Promise<ArrayBuffer>): Promise<void> {
    this.seedingAudio = true;
    try {
      const buffer = await seed.catch(() => new ArrayBuffer(0));
      if (!this.active || !this.socket) {
        return;
      }
      if (buffer.byteLength > 0) {
        const frameBytes = VoiceController.AUDIO_SEED_FRAME_BYTES;
        for (let offset = 0; offset < buffer.byteLength; offset += frameBytes) {
          if (!this.active || !this.socket) {
            return;
          }
          const end = Math.min(offset + frameBytes, buffer.byteLength);
          this.socket.sendAudio(buffer.slice(offset, end));
        }
      }
      this.socket.sendControl({ type: 'commit_audio' });
    } catch (error) {
      logger.debug('[voice] audio seed failed; continuing without clip', {
        errorType: error instanceof Error ? error.name : 'unknown',
      });
      if (this.active && this.socket) {
        this.socket.sendControl({ type: 'commit_audio' });
      }
    } finally {
      this.seedingAudio = false;
    }
  }

  /**
   * Enter/leave dictation mode from the UI. Optimistic only on stop: entering
   * can fail server-side (transcription unavailable), and showing "dictating"
   * over a session that is still conversational is the one state the clinician
   * must never be lied to about — they'd assume they were being recorded.
   */
  toggleDictation(target?: string): void {
    if (this.store.dictation.active) {
      this.socket?.sendControl({ type: 'stop_dictation' });
      return;
    }
    this.socket?.sendControl({
      type: 'start_dictation',
      ...(target ? { target } : {}),
    });
  }

  resolveApproval(approved: boolean, modifiedInput?: Record<string, unknown>): void {
    // Name the approval being answered. An untargeted resolve is handled
    // server-side as "resolve everything pending", which is indistinguishable
    // from the intended one while a single agent runs — and authorizes writes
    // the clinician was never shown once more than one does.
    const approvalId = this.store.approval?.approvalId;
    this.store.removeApproval(approvalId);
    this.socket?.sendControl({
      type: 'resolve_approval',
      approved,
      ...(approvalId ? { approvalId } : {}),
      ...(modifiedInput ? { modifiedInput } : {}),
    });
  }

  /**
   * Commit the workspace draft as a session document. The clinician's Create
   * click is the consent — the server authorizes + persists and answers with
   * ``workspace_update`` frames (``created``, or ``ready`` + ``error``).
   */
  createWorkspaceDocument(payload: {
    sessionId?: string;
    title?: string;
    content: string;
  }): void {
    this.socket?.sendControl({
      type: 'create_workspace_document',
      ...(payload.sessionId ? { session_id: payload.sessionId } : {}),
      ...(payload.title ? { title: payload.title } : {}),
      content: payload.content,
    });
  }

  sendGuideEvent(event: {
    guideId: string;
    stepId?: string;
    status: 'advanced' | 'finished' | 'target_missing';
    nextSay?: string;
    title?: string;
    target?: string;
    reason?: string;
    explanation?: string;
    requires?: string[];
    fallbackTarget?: string | null;
    snapshot?: { path: string; visible: string[] };
  }): void {
    this.socket?.sendControl({
      type: 'guide_event',
      guideId: event.guideId,
      ...(event.stepId ? { stepId: event.stepId } : {}),
      status: event.status,
      ...(event.nextSay ? { nextSay: event.nextSay } : {}),
      ...(event.title ? { title: event.title } : {}),
      ...(event.target ? { target: event.target } : {}),
      ...(event.reason ? { reason: event.reason } : {}),
      ...(event.explanation ? { explanation: event.explanation } : {}),
      ...(event.requires?.length ? { requires: event.requires } : {}),
      ...(event.fallbackTarget != null ? { fallbackTarget: event.fallbackTarget } : {}),
      ...(event.snapshot ? { snapshot: event.snapshot } : {}),
    });
  }

  private currentHint(): VoiceContextHint {
    const jotaiStore = getDefaultStore();
    const route = getVoiceAppHost().readRoute();
    const routePatientId = route.query?.id;
    const onPatientsPage = route.pathname.startsWith('/patients/');
    return buildVoiceContextHint({
      pathname: route.pathname,
      routeSessionId: getCurrentSessionId(),
      routePatientProfileId:
        onPatientsPage && typeof routePatientId === 'string' ? routePatientId : undefined,
      openArtifact: jotaiStore.get(openArtifactAtom),
      linkedItems: jotaiStore.get(composerLinkedItemsAtom),
      activeTab: jotaiStore.get(voiceActiveTabAtom),
    });
  }

  /** Push a context_update when the working session/patient/surface/tab changes. */
  private pushContextUpdate(): void {
    if (!this.socket) return;
    const hint = this.currentHint();
    const key = voiceContextHintKey(hint);
    if (key === this.lastContextHintKey) return;
    this.lastContextHintKey = key;
    this.socket.sendControl({
      type: 'context_update',
      ...(hint.sessionId ? { sessionId: hint.sessionId } : {}),
      ...(hint.patientProfileId ? { patientProfileId: hint.patientProfileId } : {}),
      surface: hint.surface,
      ...(hint.tabKind ? { tabKind: hint.tabKind } : {}),
      ...(hint.documentId ? { documentId: hint.documentId } : {}),
      ...(hint.templateId ? { templateId: hint.templateId } : {}),
      ...(hint.templateName ? { templateName: hint.templateName } : {}),
      ...(hint.voiceStyle ? { voiceStyle: hint.voiceStyle } : {}),
    });
  }

  /** Flush playback once the user is confirmed talking over Heidi. */
  private maybeLocalBargeIn(pcm: ArrayBuffer): void {
    const samples = new Int16Array(pcm);
    if (samples.length === 0) return;
    let sumSquares = 0;
    for (let i = 0; i < samples.length; i++) {
      const v = (samples[i] ?? 0) / 0x8000;
      sumSquares += v * v;
    }
    const rms = Math.sqrt(sumSquares / samples.length);

    // Track the floor even while Heidi is quiet, so the estimate is already
    // warm when she starts speaking. Echo that leaks past AEC raises the floor
    // too, which is what keeps Heidi's own voice from barging in on herself.
    const rate = rms < this.noiseFloor ? NOISE_FLOOR_DECAY : NOISE_FLOOR_ATTACK;
    this.noiseFloor += rate * (rms - this.noiseFloor);

    const player = this.player;
    if (!player?.isPlaying || this.playbackSuppressed) {
      this.loudFrames = 0;
      this.quietFrames = 0;
      return;
    }

    const threshold = Math.min(
      BARGE_IN_MAX_THRESHOLD,
      Math.max(BARGE_IN_MIN_THRESHOLD, this.noiseFloor * BARGE_IN_FLOOR_MARGIN)
    );
    if (rms < threshold) {
      this.quietFrames += 1;
      if (this.quietFrames >= BARGE_IN_QUIET_RESET) {
        this.loudFrames = 0;
      }
      return;
    }
    this.quietFrames = 0;
    this.loudFrames += 1;
    if (this.loudFrames >= BARGE_IN_CONFIRM_FRAMES) {
      this.loudFrames = 0;
      this.quietFrames = 0;
      // Hold the mute until the BE finishes / cancels this turn — otherwise
      // the next PCM chunk from Heidi would start playing again immediately.
      this.playbackSuppressed = true;
      player.flush();
    }
  }

  private handleControl(message: ServerMessage): void {
    const store = this.store;
    switch (message.type) {
      case 'ready':
        store.setConnection('active');
        this.armGreetingFallback();
        break;
      case 'state':
        store.setSessionState(message.state);
        if (!store.greetingComplete && message.state === 'speaking') {
          this.greetingStarted = true;
          // Greeting began — do not flip status via the missing-greeting fallback.
          this.clearGreetingFallback();
        } else if (
          !store.greetingComplete &&
          this.greetingStarted &&
          message.state === 'listening'
        ) {
          this.greetingResponseDone = true;
          this.maybeCompleteGreeting();
        }
        // Lift the local barge mute when Heidi is idle again or starts a new
        // spoken reply — not on `thinking`, or in-flight PCM from the cancelled
        // turn can sneak through before `speaking`.
        if (message.state === 'listening' || message.state === 'speaking') {
          this.playbackSuppressed = false;
        }
        break;
      case 'transcript':
        store.appendTranscript(message.role, message.text);
        break;
      case 'evidence':
        store.upsertEvidence(message.callId, {
          text: message.text,
          sources: message.sources,
        });
        break;
      case 'tool':
        store.upsertTool(
          message.name,
          message.status,
          message.callId,
          message.title,
          message.agentId
        );
        if (message.status === 'completed') {
          // Prefer canvas/linked session on Work — route id is usually empty there.
          const sessionId = this.currentHint().sessionId ?? getCurrentSessionId();
          const hint = this.currentHint();

          invalidateCachesForAgentToolWrite({
            queryClient: getVoiceAppHost().getQueryClient(),
            toolName: message.name,
            sessionId,
            patientProfileId: hint.patientProfileId,
            invalidateSessionsList: () => void invalidateSessionsQuery(),
          });
          // Legacy openArtifact hint on tool frames (Heidi Work only).
          if (
            message.openArtifact &&
            isHeidiWorkRoute(getVoiceAppHost().readRoute().pathname)
          ) {
            applyConfiguredVoiceUiAction({
              action: 'open_artifact',
              data: message.openArtifact,
            });
          }
        }
        break;
      case 'ui':
        if (message.action === 'navigate') {
          const ok = applyConfiguredVoiceUiAction({
            action: 'navigate',
            data: message.data,
          });
          // Tool lifecycle already covers allowlisted ui.* tools — skip a second row.
          if (!hasToolForUiAction('navigate', store.tools)) {
            store.appendUiEvent(
              'navigate',
              `${ok ? 'ok' : 'failed'} href=${message.data.href}`
            );
          }
        } else if (message.action === 'open_artifact') {
          // Canvas apply is not Heidi-Work-gated; legacy tool.openArtifact hint above is.
          const ok = applyConfiguredVoiceUiAction({
            action: 'open_artifact',
            data: message.data,
          });
          const data = message.data;
          if (!hasToolForUiAction('open_artifact', store.tools)) {
            store.appendUiEvent(
              'open_artifact',
              `${ok ? 'ok' : 'failed'} type=${data.type}` +
                (data.session_id ? ` session=${data.session_id}` : '') +
                (data.document_id ? ` document=${data.document_id}` : '')
            );
          }
        } else if (message.action === 'open_workspace') {
          const ok = applyConfiguredVoiceUiAction({
            action: 'open_workspace',
            data: message.data,
          });
          store.appendUiEvent(
            'open_workspace',
            `${ok ? 'ok' : 'failed'} ${message.data.component?.document_kind ?? ''}`
          );
        } else if (message.action === 'workspace_update') {
          // High-frequency streaming frames — apply silently (no timeline row
          // per chunk; the workspace steps rail is the visible progress).
          applyConfiguredVoiceUiAction({
            action: 'workspace_update',
            data: message.data,
          });
        } else if (message.action === 'present_info') {
          // The present_information tool frame already logs the activity —
          // applying silently avoids a duplicate dock row per card.
          applyConfiguredVoiceUiAction({ action: 'present_info', data: message.data });
        } else if (message.action === 'toast') {
          const ok = applyConfiguredVoiceUiAction({
            action: 'toast',
            data: message.data,
          });
          if (!hasToolForUiAction('toast', store.tools)) {
            store.appendUiEvent(
              'toast',
              `${ok ? 'ok' : 'failed'} ${message.data.variant}: ${
                message.data.title ?? message.data.body ?? ''
              }`
            );
          }
        } else if (message.action === 'highlight') {
          const ok = applyConfiguredVoiceUiAction({
            action: 'highlight',
            data: message.data,
          });
          if (!hasToolForUiAction('highlight', store.tools)) {
            store.appendUiEvent(
              'highlight',
              `${ok ? 'ok' : 'failed'} target=${message.data.target}`
            );
          }
        } else if (message.action === 'open_template_picker') {
          const ok = applyConfiguredVoiceUiAction({ action: 'open_template_picker' });
          if (!hasToolForUiAction('open_template_picker', store.tools)) {
            store.appendUiEvent('open_template_picker', ok ? 'ok' : 'failed');
          }
        } else if (message.action === 'start_guide') {
          const ok = applyConfiguredVoiceUiAction({
            action: 'start_guide',
            data: message.data,
          });
          if (!hasToolForUiAction('start_guide', store.tools)) {
            store.appendUiEvent(
              'start_guide',
              `${ok ? 'ok' : 'failed'} id=${message.data.id}`
            );
          }
        } else {
          // Fail closed: do not coerce unknown wire actions into toast.
          const unexpected = message as { action?: string };
          logger.warn('voice: unknown ui action', { action: unexpected.action });
        }
        break;
      case 'approval_request':
        store.addApproval({
          tool: message.tool,
          summary: message.summary,
          ...(message.toolName ? { toolName: message.toolName } : {}),
          ...(message.toEmails ? { toEmails: message.toEmails } : {}),
          ...(message.approvalId ? { approvalId: message.approvalId } : {}),
          ...(message.agentId ? { agentId: message.agentId } : {}),
        });
        store.appendApprovalEvent('request', message.tool, message.summary);
        break;
      case 'approval_resolved': {
        // Resolved server-side (spoken yes/no, a Confirm tap, or a timeout).
        // Drop the one it names; an unnamed resolve means the head, which is
        // what a single-agent server has always sent.
        const resolved = message.approvalId
          ? store.approvals.find((a) => a.approvalId === message.approvalId)
          : store.approval;
        store.appendApprovalEvent(
          'resolved',
          resolved?.tool ?? '(unknown)',
          resolved?.summary ?? ''
        );
        store.removeApproval(message.approvalId);
        break;
      }
      case 'dictation':
        if (message.state === 'started') {
          store.startDictation();
          // Drop what was queued before the switch — Heidi is deaf now, so a
          // half-played sentence would talk over a clinician who cannot
          // interrupt back. Playback is *not* suppressed: the server still
          // speaks one "go ahead" confirmation after this frame, and the
          // silence guarantee for everything else is enforced server-side by
          // routing audio away from the realtime model.
          this.player?.flush();
        } else if (message.state === 'transcript') {
          store.appendDictation(message.text ?? '', message.final ?? false);
        } else {
          store.stopDictation(message.text ?? '', message.reason ?? 'requested');
        }
        break;
      case 'clear_playback':
        this.playbackSuppressed = true;
        this.player?.flush();
        break;
      case 'chat_exported':
        this.openContinuationChat(message.chatId, message.sessionId ?? null);
        break;
      case 'error':
        store.setError(message.message);
        break;
      case 'closing':
        void this.stop();
        break;
    }
  }

  /**
   * Voice → text continuity. After a deliberate hang-up the server seeds a
   * session-linked Work project with the call transcript and announces it
   * here (the socket lingers after `end` precisely so this frame can
   * arrive). Point the session's Work panel at that project and open the
   * RHS so the conversation visibly picks up in text — this moment is how
   * users discover the handoff exists.
   */
  private openContinuationChat(chatId: string, sessionId: string | null): void {
    // The Work panel is per scribe session; a standalone project (voice
    // call with no grounded session) has no panel surface to open here.
    if (!sessionId) return;
    // Don't yank the panel open on an unrelated page — only continue the
    // conversation where it belongs.
    if (getCurrentSessionId() !== sessionId) return;
    const jotaiStore = getDefaultStore();
    // Refetch the project lists (session history switcher + sidebars) so
    // the server-created id resolves, then point the panel at it — the same
    // atom pair useCreateSessionWorkProject writes on a client-side create.
    invalidateProjectLists(getVoiceAppHost().getQueryClient());
    jotaiStore.set(workPanelProjectIdAtomFamily(sessionId), chatId);
    jotaiStore.set(activeWorkPanelProjectIdAtom, {
      sessionId,
      projectId: chatId,
    });
    // On a session page an open RHS becomes the voice paint host, so opening
    // it under an expanded voice window would re-home that window into the
    // very panel meant to show the chat — voice must keep its own floating
    // chrome. The call just ended, so collapse the workspace to its pill (the
    // same move the post-commit auto-minimize makes) before opening the panel.
    this.store.minimizeWorkspace();
    getVoiceAppHost().openEvidenceSidebar();
  }

  private maybeCompleteGreeting(): void {
    if (
      !this.store.greetingComplete &&
      this.greetingStarted &&
      this.greetingResponseDone &&
      !this.player?.isPlaying
    ) {
      this.clearGreetingFallback();
      this.store.setGreetingComplete(true);
    }
  }

  /** Mirror VoiceSocket's reconnectTimer lifecycle: one handle, cleared on stop. */
  private clearGreetingFallback(): void {
    if (this.greetingFallbackTimer != null) {
      clearTimeout(this.greetingFallbackTimer);
      this.greetingFallbackTimer = null;
    }
  }

  /**
   * Arm (or refresh) the missing-greeting fallback once the connection is
   * active. No-ops if the greeting already started or completed; clears any
   * prior timer so end/reconnect cannot leave a stale fire.
   */
  private armGreetingFallback(): void {
    this.clearGreetingFallback();
    if (!this.active || this.store.greetingComplete || this.greetingStarted) {
      return;
    }
    this.greetingFallbackTimer = setTimeout(() => {
      this.greetingFallbackTimer = null;
      if (!this.active || this.store.greetingComplete || this.greetingStarted) {
        return;
      }
      this.store.setGreetingComplete(true);
    }, GREETING_FALLBACK_MS);
  }
}

export const voiceController = new VoiceController();
